import { err, ok, type Result } from '../result.ts';
import { formatUrl, parseUrl } from './urls.ts';

/**
 * R2 — two fields, never derived from one another.
 *
 * `instanceUrl` is what the user sees and what out-links point at; `transportBaseUrl` is where
 * requests actually go. With direct cross-origin access (§3) the two are equal, and keeping them
 * separate costs nothing while preserving the display-vs-fetch distinction Android needs.
 */
export interface InstanceConfig {
  readonly instanceUrl: string;
  readonly transportBaseUrl: string;
}

export type InstanceUrlProblem = 'empty' | 'not-absolute' | 'unsupported-scheme' | 'has-query';

export interface InstanceConfigError {
  readonly field: 'instanceUrl' | 'transportBaseUrl';
  readonly problem: InstanceUrlProblem;
}

/**
 * Canonical form: lowercase scheme and host, default port removed, no trailing slash, no query or
 * fragment. A base *path* is allowed — an instance may live at `https://host/invidious`.
 */
export function normalizeInstanceUrl(input: string): Result<string, InstanceUrlProblem> {
  const trimmed = input.trim();
  if (trimmed === '') return err('empty');

  const parts = parseUrl(trimmed);
  if (!parts || !parts.absolute) return err('not-absolute');
  if (parts.scheme !== 'http' && parts.scheme !== 'https') return err('unsupported-scheme');
  if (parts.query !== '' || parts.fragment !== '') return err('has-query');

  let path = parts.path;
  while (path.endsWith('/')) path = path.slice(0, -1);

  return ok(formatUrl({ ...parts, path, query: '', fragment: '' }));
}

/**
 * Both fields are required. Callers with direct access pass the same value twice — deliberately,
 * so that "transport happens to equal instance" stays a decision at the boundary rather than a
 * derivation buried in core (R2).
 */
export function createInstanceConfig(input: {
  instanceUrl: string;
  transportBaseUrl: string;
}): Result<InstanceConfig, InstanceConfigError> {
  const instanceUrl = normalizeInstanceUrl(input.instanceUrl);
  if (!instanceUrl.ok) return err({ field: 'instanceUrl', problem: instanceUrl.error });

  const transportBaseUrl = normalizeInstanceUrl(input.transportBaseUrl);
  if (!transportBaseUrl.ok) return err({ field: 'transportBaseUrl', problem: transportBaseUrl.error });

  return ok({ instanceUrl: instanceUrl.value, transportBaseUrl: transportBaseUrl.value });
}
